/**
 * The one place the Rater X recalculate payload is spelled out.
 *
 * Three call sites ask the same endpoint the same question — the live
 * recalculate loop (`useUwRaterXRecalculate`), the Moo preview
 * (`moo/capabilities/previewCapability`), and the draft's proposed rate
 * (`draft/useDraftRate`) — and all three had their own hand-copied list of
 * ~28 fields. They had already drifted once: the preview hardcoded
 * `strictMode: false` and priced coverage-only proposals in a mode the apply
 * would never use, which showed up as a preview promising a $7,672 saving on a
 * $52 change. The two things that genuinely differ between the call sites are
 * parameters here, so a fourth copy is never the way to express a difference.
 */
import { raterXRecalculatePayloadBuilder } from '../../../utils/rater-x/helpers';
import type { UnderwriteQuoteState } from '../types';

/**
 * The rating inputs a caller may supply from somewhere other than `state`.
 *
 * Only the preview does: it replays the proposed commands through the real
 * reducer and prices the result, without the underwriter's screen changing
 * underneath them. A replayed `UnderwriteQuoteState` is a structural superset
 * of this interface, so it satisfies it directly — no separate shape to keep
 * in sync.
 */
export interface RecalculateRatingInputs {
  readonly effectiveDate: string;
  readonly endDate: string;
  readonly global: UnderwriteQuoteState['global'];
  readonly coverageState: UnderwriteQuoteState['coverageState'];
  readonly ratingOptions: UnderwriteQuoteState['ratingOptions'];
  /**
   * A suite part's Limit and Retention (and the `lockValues` an edit to them
   * sets). Rating inputs like the policy's, so a replayed proposal supplies
   * them too.
   */
  readonly componentGlobals: UnderwriteQuoteState['componentGlobals'];
  readonly carrierPrefix?: string;
  readonly treatyId?: string;
}

/**
 * @param state the quote the rest of the payload is read from.
 * @param strictMode `false` lets the rater snap out-of-table values to the
 *   nearest valid option. All three callers pass `!globalChanged` off the
 *   state they are actually pricing: the live loop and the draft rate read it
 *   off their own state, and the preview reads it off the state produced by
 *   replaying the proposal through the real reducer — so by the time this is
 *   called, `globalChanged` already reflects the proposal, and there is
 *   nothing left to fold in here. Explicit rather than derived here, so no
 *   caller can be priced in a mode a different caller would not have used.
 * @param inputs rating inputs to price INSTEAD of the ones on `state`.
 *   Defaults to `state`'s own, which is what the two live callers want.
 */
export function toRecalculatePayload(
  state: UnderwriteQuoteState,
  strictMode: boolean,
  inputs: RecalculateRatingInputs = state
) {
  return raterXRecalculatePayloadBuilder({
    effectiveDate: inputs.effectiveDate,
    endDate: inputs.endDate,
    productType: state.productType,
    fieldValues: state.fieldValues,
    account: state.account,
    risk: state.risk,
    policyValue: inputs.global?.value,
    coverages: inputs.coverageState,
    ratingOptions: inputs.ratingOptions,
    strictMode,
    isRenewal: state.isRenewal,
    isUwView: true,
    productVersion:
      state.quote.quote.raterResponse?.raterResponseFlags.productVersion,
    commissionRate: state.commissionRate,
    quoteCommissionRate: state.quoteCommissionRate,
    isAgencyBilled: state.isAgencyBilled,
    categoryCustomizations: state.categoryCustomizations,
    agencyName: state.agencyName,
    marketType: state.marketType,
    agencyId: state.agencyId,
    accountId: state.account.id,
    componentGlobals: inputs.componentGlobals,
    quoteComponentsRaterResponse: state.quote?.quote.raterResponse?.components,
    brokerFee: state.brokerFee,
    carrierPrefix: inputs.carrierPrefix,
    treatyId: inputs.treatyId,
  });
}
